import { useMemo } from 'react';
import { useAuth } from '@shared/components/AuthProvider';

type UseCanIntroduceWebsiteV2ReturnType = Readonly<{
  value: boolean;
  isLoading: boolean;
}>;

/**
 * The one answer to "may we tell this user about Website V2?". Every surface that invites a user
 * into V2 — the dashboard introduction and its mini reminder, the parked createwedding funnel
 * choice — reads this and nothing else. The Website V1 / V2 switch in Settings → Website is
 * separate: it follows the event's enrollment (`useCanSwitchWebsiteVersion`).
 *
 * Today it wraps super-admin, so the introduction stays team-only. Rollout plan: swap the body for
 * a remote feature flag when V2 opens to couples (call sites already honour `isLoading`), then
 * delete the hook together with the introduction once V2 is the default.
 *
 * Distinct from `useWebsiteV2Feature` (is THIS EVENT on V2) and from the
 * `websiteV2IntroductionSeen` consent key (have we already told this user, for this event).
 */
export const useCanIntroduceWebsiteV2 = (): UseCanIntroduceWebsiteV2ReturnType => {
  const { isSuperAdmin, isCurrentUserSettled } = useAuth();

  return useMemo(
    () => ({
      value: isSuperAdmin,
      isLoading: !isCurrentUserSettled,
    }),
    [isSuperAdmin, isCurrentUserSettled],
  );
};
